import React, { useState, useContext, useEffect } from "react";
import styled from "styled-components";
import Button from "react-bootstrap/Button";
import Alert from "react-bootstrap/Alert";
import Spinner from "react-bootstrap/Spinner";
import { buildEnvironmentSelectionConfig } from "../HelperFunctions";
import { authenticate } from "../apiHandlers/authentication/Authenticate";
import { AppMainContext } from "../App";
import logo from "../assets/logo.svg";

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #f5f5f5;
  background-size: cover;
  height: 100vh;
  width: 100vw;
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  background-color: #FFFFFF;
  box-shadow: 0px 15px 30px rgba(0, 0, 0, 0.05);
  border-radius: 15.0104px;
  padding: 30px;
  max-width: 450px;
  width: 100%;
  height : 700px;
  max-height : 100%;
  0px 15px;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 15px;
  padding-bottom: 4px;
  margin-bottom: 10px;
  border: 0px solid #ddd;
  background: #fafbfd;
  border-radius: 3.00208px;
  margin-bottom: 20px;
`;

const RowContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  align-items: center;
`;

const ImgContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  width: 50px;
  height: 50px;
  border-radius: 10px;
`;

const CompanyTitle = styled.div`
  font-family: "Inter";
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 19px;
  color: #6285ff;
`;

const Message = styled.div`
  font-family: "Inter";
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  letter-spacing: 0.03em;
  color: rgba(0, 0, 0, 0.5);
  margin-left: 10px;
`;

const LoginTitle = styled.div`
  font-family: "Inter";
  font-style: normal;
  font-weight: 600;
  font-size: 42px;
  line-height: 51px;
  color: #000000;
  margin-left: 10px;
  margin-bottom: 20px;
`;

const StyledLogo = styled.img`
  display: flex;
  height: 100%;
  width: auto;
`;
const LoginButton = styled(Button)`
  width: 45%;
  border-radius: 5px;
  background-color: #6285ff;
  padding: 10px 20px;
`;

const ErrorAlert = styled(Alert)`
  display: flex;
  width: 100%;
  margin-bottom: 0px;
  display: ${(props) => (props.isDisplayed ? "block" : "none")};
`;

const InputTitle = styled.div`
  font-family: "Inter";
  font-style: normal;
  font-weight: 400;
  font-size: 12.5px;
  line-height: 15px;
  color: #000000;
`;

const SignInMessage = styled.div`
  font-family: "Inter";
  font-style: normal;
  font-weight: 600;
  font-size: 13px;
  line-height: 18px;
  text-align: center;
  text-transform: uppercase;
`;

const FlagOption = styled.div`
  display: flex;
  align-items: center;
  padding: 8px; // Adjusted for a sleeker look
  border-radius: 5px;
  cursor: pointer;

  &:hover {
    background-color: #eaeaea;
  }
`;

const FlagImg = styled.img`
  width: 25px; // Increased size
  height: 25px; // Increased size
  border-radius: 20px; // More rounded corners
  margin-right: 10px;
`;

const FlagSelect = styled.div`
  width: 100%;
  padding: 0px; // Reduced padding
  margin-bottom: 20px;
  border: 1px solid #ddd;
  background: #fafbfd;
  border-radius: 3.00208px;
  cursor: pointer;
  position: relative;
  font-size: 14px; // Adjust if needed
`;
const Dropdown = styled.div`
  position: absolute;
  background-color: #f9f9f9;
  min-width: 100%;
  overflow: auto;
  z-index: 1;
  border-radius: 3.00208px;
  box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.2);
`;

export default function Login() {
  const { appContext, setAppContext } = useContext(AppMainContext);
  const [creds, setCreds] = useState({ pw: null, org: null, pc: null });
  const [selectedEnvironment, setSelectedEnvironment] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [spinnerDisplay, setSpinnerDisplay] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState(
    "There was an error logging in. Please check your credentials and try again."
  );
  const token = appContext.token;
  const org = appContext.org;
  const planningCycle = appContext.planningCycle;
  const urlOptions = buildEnvironmentSelectionConfig();
  const url = selectedEnvironment?.value;

  useEffect(() => {
    urlOptions.length && setSelectedEnvironment(urlOptions[0]);
  }, []);

  const handleInputChange = (id, event) => {
    const updatedCreds = creds;
    updatedCreds[id] = event.target.value;
    setCreds(updatedCreds);
    const updatedAppContext = {
      ...appContext,
      token: updatedCreds.pw,
      org: updatedCreds.org,
      planningCycle: updatedCreds.pc,
    };
    setAppContext(updatedAppContext);
  };

  const handleFlagSelect = (option) => {
    setSelectedEnvironment(option);
    setShowDropdown(false);
  };

  const checkAllFieldsValid = () => {
    if (!creds.pw || !creds.org || !creds.pc) {
      setAlertMessage("Make sure none of your fields are empty");
      setShowAlert(true);
      return false;
    }
    return true;
  };

  const authenticateUserFromToken = async () => {
    const canAuthenticate = checkAllFieldsValid();
    if (!canAuthenticate) return;
    setSpinnerDisplay(true);
    setShowAlert(false); // Reset alert display each time login is attempted.
    const returnedUserParams = await authenticate(
      url,
      token,
      org,
      planningCycle
    );
    if (
      returnedUserParams?.stateObject?.error === true ||
      returnedUserParams?.stateObject === undefined
    ) {
      const stateObject = returnedUserParams.stateObject;
      const errorMessage = stateObject
        ? stateObject.message
        : "An unexpected error occurred";
      setAlertMessage(errorMessage);
      setShowAlert(true); // Display the alert if there is an error.
    } else {
      setAppContext({ ...appContext, ...returnedUserParams, url });
    }
    setSpinnerDisplay(false);
  };

  return (
    <Container>
      <Card>
        <RowContainer>
          <ImgContainer>
            <StyledLogo src={logo} alt="My Description" />
          </ImgContainer>
          <CompanyTitle>Sales Planning Autopilot</CompanyTitle>
        </RowContainer>
        <RowContainer>
          <Message>Welcome back to Sales Planning Autopilot</Message>
        </RowContainer>
        <RowContainer></RowContainer>
        <RowContainer>
          <LoginTitle>Sign In</LoginTitle>
        </RowContainer>
        <RowContainer>
          <InputTitle>Select your region</InputTitle>
        </RowContainer>
        <FlagSelect onClick={() => setShowDropdown(!showDropdown)}>
          {selectedEnvironment ? (
            <FlagOption>
              <FlagImg
                src={selectedEnvironment.flag}
                alt={`${selectedEnvironment} Flag`}
              />
              {selectedEnvironment.name}
            </FlagOption>
          ) : (
            "Select Region"
          )}
          {showDropdown && (
            <Dropdown>
              {urlOptions.map((option) => (
                <FlagOption
                  key={option.key}
                  onClick={() => handleFlagSelect(option)}
                >
                  <FlagImg src={option.flag} alt={`${option.name} Flag`} />
                  {option.name}
                </FlagOption>
              ))}
            </Dropdown>
          )}
        </FlagSelect>

        <RowContainer>
          <InputTitle>Token</InputTitle>
        </RowContainer>
        <RowContainer>
          <StyledInput
            placeholder="Type the token"
            value={creds.pw}
            type="password"
            onChange={(event) => handleInputChange("pw", event)}
          />
        </RowContainer>
        <RowContainer>
          <InputTitle>Organisation Name</InputTitle>
        </RowContainer>
        <RowContainer>
          <StyledInput
            placeholder="Type the organisation name"
            value={creds.org}
            onChange={(event) => handleInputChange("org", event)}
          />
        </RowContainer>
        <RowContainer>
          <InputTitle>Planning Cycle</InputTitle>
        </RowContainer>
        <RowContainer>
          <StyledInput
            placeholder="Type the planning cycle"
            value={creds.pc}
            onChange={(event) => handleInputChange("pc", event)}
          />
        </RowContainer>
        <RowContainer>
          <ErrorAlert
            variant="danger"
            onClose={() => setShowAlert(false)}
            isDisplayed={showAlert}
          >
            {alertMessage}
          </ErrorAlert>
        </RowContainer>
        <LoginButton
          onClick={authenticateUserFromToken}
          disabled={spinnerDisplay}
        >
          {spinnerDisplay ? (
            <Spinner animation="border" variant="light" size="sm" />
          ) : (
            <SignInMessage>Sign In</SignInMessage>
          )}
        </LoginButton>
      </Card>
    </Container>
  );
}
